import { cn } from '@/ui/lib/utils'
import type { ReturnTone } from '@/ui/lib/quantFormat'

export interface QuantSparklineProps {
  /** [x, y] pairs already mapped into a `width` x `height` box. Empty reserves the cell. */
  points: Array<[number, number]>
  /** y of the dashed baseline (starting equity). */
  baselineY: number
  width: number
  height: number
  tone: ReturnTone
  className?: string
}

const STROKE: Record<ReturnTone, string> = {
  up: 'var(--accent-line)',
  down: 'var(--alert)',
  flat: 'var(--paper-50)',
  none: 'var(--paper-50)',
}

/**
 * Equity sparkline (spec 036 handoff 3.4): a dashed baseline plus a polyline
 * coloured by return sign. The caller maps values to points; this only paints.
 * With no points it still reserves its fixed cell so rows never resize.
 */
export function QuantSparkline({ points, baselineY, width, height, tone, className }: QuantSparklineProps) {
  const style = { width, height }
  if (points.length === 0) return <span aria-hidden="true" className={cn('block shrink-0', className)} style={style} />
  const attr = points.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ')
  return (
    <svg
      aria-hidden="true"
      data-sparkline
      data-points={points.length}
      viewBox={`0 0 ${width} ${height}`}
      className={cn('block shrink-0 overflow-visible', className)}
      style={style}
    >
      <line
        x1={0}
        x2={width}
        y1={baselineY}
        y2={baselineY}
        stroke="var(--paper-14)"
        strokeDasharray="2 3"
        vectorEffect="non-scaling-stroke"
      />
      <polyline points={attr} fill="none" stroke={STROKE[tone]} strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
    </svg>
  )
}
